<template>
  <main>
    <mdb-container class="mt-3 mb-3">
      <mdb-btn
        :aria-label="$t('gallery.all_albums')"
        class="pentagon-back"
        floating
        href="/Gallerij"
        size="lg"
        tag="a"
        @click.native="back"
      ><mdb-icon icon="arrow-left"/></mdb-btn>

      <content-status
        v-if="!album"
        :empty-message="emptyMessage"
        :error-message="$t('gallery.album_load_error')"
        :failed="page.error"
        :loading="isLoading"
        :loading-label="$t('site.loading')"
        @retry="load"
      />

      <template v-else>
        <h1 class="pentagon-album-heading" data-cy="intro-album-title">{{ album.title }}</h1>
        <p v-if="album.caption" class="pentagon-album-heading-caption text-center">
          {{ album.caption }}
        </p>

        <p v-if="album.description" class="pentagon-album-heading-description text-center">
          {{ album.description }}
        </p>
        <p
          v-if="!album.photos.length"
          class="pentagon-empty text-center"
          data-cy="intro-album-empty"
        >
          {{ $t('gallery.album_empty') }}
        </p>

        <mdb-row v-else class="pentagon-photos" data-cy="intro-photos">
          <mdb-col
            v-for="(photo, index) in album.photos"
            :key="photo.thumb"
            class="pentagon-photo-column"
            col="6"
            md="4"
            xl="3"
          >
            <a
              :aria-label="openLabel(index)"
              :href="photo.large"
              class="pentagon-photo"
              data-cy="intro-photo"
              @click="openLightbox(index, $event)"
            >
              <img
                :alt="photoAlt(photo, index)"
                :height="photo.thumb_height"
                :src="photo.thumb"
                :width="photo.thumb_width"
                class="pentagon-photo__image"
                loading="lazy"
              >
            </a>
          </mdb-col>
        </mdb-row>

        <photo-lightbox
          :index="lightboxIndex"
          :photos="lightboxPhotos"
          :visible="lightboxVisible"
          @hide="lightboxVisible = false"
        />
      </template>
    </mdb-container>
  </main>
</template>

<script>
import {
  mdbContainer, mdbRow, mdbCol, mdbIcon, mdbBtn,
} from 'mdbvue';
import ContentStatus from '../../../components/ContentStatus.vue';
import PhotoLightbox from '../../../components/PhotoLightbox.vue';

export default {
  name: 'Show',
  components: {
    ContentStatus,
    PhotoLightbox,
    mdbContainer,
    mdbRow,
    mdbCol,
    mdbIcon,
    mdbBtn,
  },
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      lightboxVisible: false,
      lightboxIndex: 0,
    };
  },
  computed: {
    page() {
      return this.$store.getters['intro/albumPage'](this.slug);
    },
    album() {
      return this.page.data;
    },
    // The request starts in created(), so without data, error or 404 it is on its way.
    isLoading() {
      return !this.page.data && !this.page.error && !this.page.notFound;
    },
    emptyMessage() {
      return this.page.notFound ? this.$t('gallery.album_not_found') : '';
    },
    lightboxPhotos() {
      return this.album.photos.map((photo, index) => ({
        src: photo.large,
        caption: this.photoAlt(photo, index),
      }));
    },
  },
  watch: {
    slug() {
      this.lightboxVisible = false;
      this.load();
    },
  },
  created() {
    this.$store.commit('TOGGLE_OFF_NAVBAR_TRANSPARENT');
    this.load();
  },
  methods: {
    load() {
      this.$store.dispatch('intro/fetchAlbum', this.slug);
    },
    // The description an administrator gave, or a generic one.
    photoAlt(photo, index) {
      return photo.alt || this.$t('gallery.photo_alt', {
        album: this.album.title,
        number: index + 1,
        total: this.album.photos.length,
      });
    },
    openLabel(index) {
      return this.$t('gallery.open_photo', { number: index + 1, total: this.album.photos.length });
    },
    // The photo's link points at the large image; a plain click opens it in the lightbox instead.
    openLightbox(index, event) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) {
        return;
      }

      event.preventDefault();
      this.lightboxIndex = index;
      this.lightboxVisible = true;
    },
    // A plain click stays in the app; a click with a modifier key keeps the link's own behaviour.
    back(event) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) {
        return;
      }

      event.preventDefault();
      this.$router.push({ name: 'Gallerij' });
    },
  },
};
</script>

<style>
  .pentagon-back.btn-floating {
    background: var(--pentagon-forest) !important;
    width: 48px;
    height: 48px;
    padding: 0;
    border: 1px solid rgba(255, 215, 0, 0.45);
    border-radius: 50%;
    color: var(--pentagon-ivory);
    line-height: 46px;
  }

  .pentagon-album-heading {
    font-size: clamp(1.4rem, 7vw, 2.5rem);
    margin-top: 1rem;
    overflow-wrap: break-word;
    text-align: center;
  }

  .pentagon-album-heading-caption {
    font-style: italic;
  }

  .pentagon-album-heading-description {
    margin-left: auto;
    margin-right: auto;
    max-width: 40rem;
  }

  .pentagon-photos {
    margin-top: 1rem;
  }

  .pentagon-photo-column {
    margin-bottom: 1rem;
  }

  .pentagon-photo {
    appearance: none;
    background: var(--pentagon-ink-raised);
    color: inherit;
    border: 1px solid transparent;
    border-radius: 2px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45);
    cursor: pointer;
    display: block;
    padding: 0;
    transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease;
    width: 100%;
  }

  .pentagon-photo__image {
    aspect-ratio: 3 / 2;
    display: block;
    height: auto;
    object-fit: cover;
    width: 100%;
  }

  .pentagon-photo:focus-visible {
    border-color: var(--pentagon-gold);
    outline: 2px solid var(--pentagon-gold);
    outline-offset: 2px;
  }

  /* Pointer devices: the photo floats up. */
  @media (hover: hover) {
    .pentagon-photo:hover {
      border-color: rgba(255, 215, 0, 0.45);
      box-shadow: 0 14px 28px rgba(0, 0, 0, 0.7);
      transform: translateY(-6px);
    }
  }

  /* Touch devices: no lift, but a press state. */
  @media (hover: none) {
    .pentagon-photo:active {
      border-color: rgba(255, 215, 0, 0.45);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .pentagon-photo {
      transition: none;
    }

    .pentagon-photo:hover {
      transform: none;
    }
  }
</style>
